import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ProviderInvoiceStateEnum } from 'src/app/enum/provider-invoice-state-enum';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ProviderInvoiceDetailModel } from 'src/app/models/shopping/provider-invoice-detail.model';
import { ProviderInvoiceModel } from 'src/app/models/shopping/provider-invoice.model';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { TaxService } from 'src/app/services/management/tax.service';
import { ProviderInvoiceDetailService } from 'src/app/services/shopping/provider-invoice-detail.service';
import { ProviderInvoiceService } from 'src/app/services/shopping/provider-invoice.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-provider-invoice-detail',
  templateUrl: './register-provider-invoice-detail.component.html',
  styleUrls: ['./register-provider-invoice-detail.component.scss'],
  standalone: false
})
export class RegisterProviderInvoiceDetailComponent implements OnInit, OnChanges {

  private readonly formBuilder = inject(FormBuilder);
  private readonly conceptInvoicingService = inject(ConceptInvoicingService);
  private readonly providerInvoiceService = inject(ProviderInvoiceService);
  private readonly providerInvoiceDetailService = inject(ProviderInvoiceDetailService);
  private readonly taxService = inject(TaxService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() permissionUpdatable: boolean = false;
  @Input() providerInvoiceDetailModel: ProviderInvoiceDetailModel = new ProviderInvoiceDetailModel();
  @Input() providerInvoiceModel: ProviderInvoiceModel = new ProviderInvoiceModel();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() providerInvoiceContract = new EventEmitter<ProviderInvoiceModel>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleCodeName: string = '';
  disableSave = false;
  formDetail: FormGroup = new FormGroup({});
  listConceptInvoicing: CustomListDropdown[] = [];
  listTaxes: CustomListDropdown[] = [];

  async ngOnInit() {
    this.validateForm(new ProviderInvoiceDetailModel());
    await this.getAllList();
  }

  async getAllList(): Promise<void> {
    this.listConceptInvoicing = await firstValueFrom(this.conceptInvoicingService.getAllPurchaseConceptsForSelectAsync());
    const taxes = await firstValueFrom(this.taxService.getIvaTaxes());
    this.listTaxes = taxes.map((tax) => ({ label: `${tax.code} - ${tax.name}`, value: tax.taxId }));
  }

  async ngOnChanges(changes: SimpleChanges) {
    const providerInvoiceDetailModel = changes['providerInvoiceDetailModel'];
    if (providerInvoiceDetailModel && !providerInvoiceDetailModel.firstChange) {
      if (providerInvoiceDetailModel.currentValue.providerInvoiceDetailId === 0 && providerInvoiceDetailModel.currentValue.getRegister) {
        const response = await firstValueFrom(this.providerInvoiceDetailService.getProviderInvoiceDetailByKeys(
          this.providerInvoiceModel.providerInvoiceId,
          this.providerInvoiceDetailModel.articleId,
          this.providerInvoiceDetailModel.conceptInvoicingId || 0
        ));
        if (response.providerInvoiceDetailId !== 0) {
          this.providerInvoiceDetailModel = response;
        } else {
          response.quantity = providerInvoiceDetailModel.currentValue.quantity;
          response.unitValue = providerInvoiceDetailModel.currentValue.unitValue;
          response.discountRate = providerInvoiceDetailModel.currentValue.discountRate;
          response.purchaseOrderDetailId = providerInvoiceDetailModel.currentValue.purchaseOrderDetailId;
          response.inventoryEntryDetailId = providerInvoiceDetailModel.currentValue.inventoryEntryDetailId;
          this.providerInvoiceDetailModel = response;
        }
        this.validateForm(this.providerInvoiceDetailModel);
        this.articleCodeName = `${this.providerInvoiceDetailModel.article?.code ?? ''} - ${this.providerInvoiceDetailModel.article?.name ?? ''}`;
      } else {
        this.validateForm(this.providerInvoiceDetailModel);
        this.articleCodeName = `${this.providerInvoiceDetailModel.article?.code ?? ''} - ${this.providerInvoiceDetailModel.article?.name ?? ''}`;
      }
    }
    const providerInvoiceModel = changes['providerInvoiceModel'];
    if (providerInvoiceModel) {
      this.disableSave = providerInvoiceModel.currentValue.state !== ProviderInvoiceStateEnum.open;
      if (this.disableSave) {
        this.formDetail.disable();
      } else {
        this.formDetail.enable();
      }
    }
  }

  calculateIvaBase() {
    const quantity = this.formDetail.get('quantity')?.value || 0;
    const unitValue = this.formDetail.get('unitValue')?.value || 0;
    const discountRate = this.formDetail.get('discountRate')?.value || 0;
    const ivaBase = quantity * unitValue * (1 - discountRate / 100);
    this.formDetail.get('ivaBase')?.setValue(ivaBase);
  }

  clearForm() {
    this.providerInvoiceDetailModel = new ProviderInvoiceDetailModel();
    this.articleCodeName = '';
    this.validateForm(this.providerInvoiceDetailModel);
    this.isFormView.emit(false);
    this.updateListDetail.emit(true);
  }

  async send() {
    Object.keys(this.formDetail.controls).forEach(key => {
      const control = this.formDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `No tiene permisos para modificar facturas proveedores.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (this.formDetail.valid) {
      const modelProviderInvoiceDetail = this.formDetail.getRawValue() as ProviderInvoiceDetailModel;
      modelProviderInvoiceDetail.providerInvoiceId = this.providerInvoiceModel.providerInvoiceId;
      try {
        if (this.providerInvoiceDetailModel.providerInvoiceDetailId === 0) {
          await firstValueFrom(this.providerInvoiceDetailService.createProviderInvoiceDetail(modelProviderInvoiceDetail));
          this.toastHandlerService.showToast('success', 'El detalle de la factura fue creado exitosamente.');
          this.updateListDetail.emit(true);
        } else {
          this.providerInvoiceDetailModel.quantity = modelProviderInvoiceDetail.quantity;
          this.providerInvoiceDetailModel.unitValue = modelProviderInvoiceDetail.unitValue;
          this.providerInvoiceDetailModel.discountRate = modelProviderInvoiceDetail.discountRate;
          this.providerInvoiceDetailModel.ivaBase = modelProviderInvoiceDetail.ivaBase;
          this.providerInvoiceDetailModel.taxId = modelProviderInvoiceDetail.taxId;
          await firstValueFrom(this.providerInvoiceDetailService.updateProviderInvoiceDetail(this.providerInvoiceDetailModel));
          this.toastHandlerService.showToast('success', 'El detalle de la factura fue modificado exitosamente.');
          this.updateListDetail.emit(true);
        }
        this.clearForm();
        this.getProviderInvoiceById(this.providerInvoiceModel.providerInvoiceId);
      } catch (error) {
        console.error('Error al guardar el detalle de la factura de proveedor.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  async getProviderInvoiceById(providerInvoiceId: number) {
    try {
      this.providerInvoiceModel = await firstValueFrom(this.providerInvoiceService.getProviderInvoiceById(providerInvoiceId));
      this.providerInvoiceContract.emit(this.providerInvoiceModel);
    } catch (error) {
      console.error('Error al buscar la factura proveedor.', error);
    }
  }

  showValidationMessages() {
    const controlMessages: any = {
      articleId: 'El campo artículo es obligatorio.',
      conceptInvoicingId: 'Debe seleccionar un concepto de compra.',
      taxId: 'El campo IVA es obligatorio.',
      quantity: 'El campo cantidad es obligatorio.',
      unitValue: 'El campo valor unitario es obligatorio.',
      discountRate: 'El campo porcentaje de descuento debe estar entre 0 y 100.',
      ivaBase: 'El campo base de IVA debe ser mayor o igual a 0.',
    };
    Object.keys(controlMessages).forEach(control => {
      if (this.formDetail.controls[control].invalid) {
        this.toastHandlerService.showToast('warning', controlMessages[control], 'Atención');
      }
    });
  }

  validateForm(providerInvoiceDetail: ProviderInvoiceDetailModel) {
    this.formDetail = this.formBuilder.group({
      articleId: [providerInvoiceDetail.articleId, [Validators.required, Validators.min(1)]],
      conceptInvoicingId: [providerInvoiceDetail.conceptInvoicingId, [Validators.required, Validators.min(1)]],
      taxId: [providerInvoiceDetail.taxId, [Validators.required, Validators.min(1)]],
      purchaseOrderDetailId: [providerInvoiceDetail.purchaseOrderDetailId],
      inventoryEntryDetailId: [providerInvoiceDetail.inventoryEntryDetailId],
      quantity: [providerInvoiceDetail.quantity, [Validators.required, Validators.min(0)]],
      unitValue: [providerInvoiceDetail.unitValue, [Validators.required, Validators.min(0)]],
      discountRate: [providerInvoiceDetail.discountRate, [Validators.min(0), Validators.max(100)]],
      ivaBase: [providerInvoiceDetail.ivaBase, [Validators.min(0)]],
    });
    if (this.disableSave) {
      this.formDetail.disable();
    } else {
      this.formDetail.enable();
    }
  }

}
